/**
 * EAA-1694: which ACH tenant the editor is currently acting in.
 *
 * An SSO identity can hold memberships in several tenants — its org may be mapped to more
 * than one — and the backend refuses to guess: with more than one membership and no
 * `X-Tenant-Id` header there is no acting tenant, so the caller has no permissions and
 * every request 403s. The editor therefore has to make a choice and send it on every call.
 *
 * Kept as a module-level value with a synchronous getter, not React state, because the
 * `configureApi` header builder in `main.tsx` runs outside React and cannot `await`. The
 * provider mirrors it into state for rendering; this module stays the single source of
 * truth that the header reads.
 */

const STORAGE_KEY = 'editor_acting_tenant_id';

export interface TenantMembership {
    tenantId: string;
    slug: string | null;
    name: string | null;
    permissions: string[];
}

let actingTenantId: string | null = readStored();
const listeners = new Set<(id: string | null) => void>();

function readStored(): string | null {
    try {
        return localStorage.getItem(STORAGE_KEY);
    } catch {
        // Private mode / blocked storage: the choice simply does not survive a reload.
        return null;
    }
}

/** Synchronous read for non-React callers (the API header builder). */
export function getActingTenantId(): string | null {
    return actingTenantId;
}

export function setActingTenantId(id: string | null): void {
    actingTenantId = id;
    try {
        if (id) {
            localStorage.setItem(STORAGE_KEY, id);
        } else {
            localStorage.removeItem(STORAGE_KEY);
        }
    } catch {
        // Non-fatal: the value still applies for this page's lifetime.
    }
    listeners.forEach((cb) => cb(id));
}

export function onActingTenantChange(cb: (id: string | null) => void): () => void {
    listeners.add(cb);
    return () => listeners.delete(cb);
}

/**
 * Reconcile the stored choice against what the identity may actually act in.
 *
 * Returns the tenant to act in, or `null` when the caller has no memberships at all.
 * A stored id that is no longer in the membership list is DISCARDED rather than sent: a
 * revoked grant must not keep being asserted, and sending it would earn a 403 on every
 * request with no hint as to why. A single membership is selected automatically — there is
 * nothing to choose, and making the user pick would be busywork.
 */
export function reconcileActingTenant(memberships: TenantMembership[]): string | null {
    if (memberships.length === 0) {
        setActingTenantId(null);
        return null;
    }
    const stored = getActingTenantId();
    if (stored && memberships.some((m) => m.tenantId === stored)) {
        return stored;
    }
    const next = memberships[0].tenantId;
    setActingTenantId(next);
    return next;
}

/**
 * EAA-1694: adopt `?tenant_id=` from the current URL as the acting tenant.
 *
 * The **embedded** editor had no acting tenant at all, and the symptom was a bare
 * `ERROR_PERMISSION_DENIED` on every ACH call — image upload included — for a user who
 * was a member of the tenant and whose role granted the permission.
 *
 * Why it happened: `setActingTenantId` was only ever called by the editor's own tenant
 * picker and by logout. The embed at `localhost:3001` is a **different origin** from the
 * host app at `localhost:3000`, so its `localStorage` is empty and the picker never runs
 * — the header was omitted. The backend then hits the deliberate rule that with more than
 * one membership and no `X-Tenant-Id` it refuses to guess (no acting tenant, zero
 * permissions), and the account in the report held three: `phuvan`, `th00078` and `emg`.
 * With one membership it would have auto-selected and nobody would have noticed.
 *
 * The host app already puts the answer in the embed URL (`?tenant_id=`), which
 * `ThemeInitializer` and `EmbedEditorPage` were already reading for other purposes. This
 * makes the API layer read it too.
 *
 * **Trusting the URL is safe.** The backend re-checks membership on every request and
 * answers 403 for a header naming a tenant the identity does not belong to, so this can
 * only ever select among tenants the caller already has. What it cannot do is grant
 * anything.
 *
 * Called from `main.tsx` **before** `configureApi`, so the very first request carries the
 * header — the failing calls in the report (`ai-types/`) fire during initial render, well
 * before any provider effect could set this.
 */
export function adoptActingTenantFromUrl(): string | null {
    if (typeof window === 'undefined') {
        return null;
    }
    let fromUrl: string | null = null;
    try {
        fromUrl = new URLSearchParams(window.location.search).get('tenant_id');
    } catch {
        return null;
    }
    if (!fromUrl) {
        // Standalone editor: leave a picker choice alone. Only the embed flow carries
        // the param, and only there is the host app the authority on which tenant.
        return actingTenantId;
    }
    if (fromUrl !== actingTenantId) {
        setActingTenantId(fromUrl);
    }
    return fromUrl;
}
